import { Exception } from '../models/Exception';
export const SUPPORTED_POLICY_VERSION = 'v1';
class PolicyAuthorizerRuleChecker {
    constructor(rule) {
        this.rule = rule;
    }
    isAuthorized({ action, resource, params }) {
        return this.isActionAuthorized(action) && this.isResourceAuthorized(resource) && this.areParamsAuthorized(params);
    }
    isActionAuthorized(action) {
        return this.rule.action === action || this.rule.action === '*';
    }
    isResourceAuthorized(resource) {
        // if resources match (or both undefined) OR its a wildcard rule, we're good
        if (this.rule.resource === resource || this.rule.resource === '*') {
            return true;
        }
        // if we didn't pass a resource, and we didn't match (undefined) in the previous check, we're not good
        if (!resource) {
            return false;
        }
        if (this.isResourceAuthorizedByWildcard(resource)) {
            return true;
        }
        return false;
    }
    /**
     * Check if a resource is authorized because the policy has a wildcard ('*') in the resource
     * Note this doesnt check for the global wildcard, which we check earlier in isResourceAuthorized
     * This is so we can support an admin policy with '*' for both action and resource that grants all permissions, even when there is no resource
     */
    isResourceAuthorizedByWildcard(resource) {
        return this.rule.resource === this.buildResourceWildcard(resource);
    }
    buildResourceWildcard(resource) {
        const resourceParts = resource.split('/');
        const resourcePrefix = resourceParts.slice(0, resourceParts.length - 1).join('/');
        return `${resourcePrefix}/*`;
    }
    areParamsAuthorized(params) {
        // If there are no param conditions, its ok
        if (!this.rule.paramConditions) {
            return true;
        }
        // Must provide all params for a rule with paramConditions
        const validationErrors = new Array();
        for (const paramKey of Object.keys(this.rule.paramConditions)) {
            if (!params || !params[paramKey]) {
                validationErrors.push(paramKey);
            }
        }
        if (validationErrors.length > 0) {
            throw new Exception('Missing params for a rule with paramConditions', 400, undefined, { validationErrors: { missingParams: Object.values(validationErrors) } });
        }
        // All params must be allowed values
        for (const paramKey of Object.keys(params)) {
            const value = params[paramKey];
            const condition = this.rule.paramConditions[paramKey];
            if (!this.isAllowedParamValue({ value, condition })) {
                return false;
            }
        }
        return true;
    }
    isAllowedParamValue({ value, condition }) {
        // Must have a value
        if (!value) {
            return false;
        }
        else if (condition === '*') {
            return true;
        }
        return value === condition;
    }
}
export class SecurityPolicyAuthorizer {
    constructor(policy) {
        // if no policy, create an authorizer that will always fail
        if (!policy) {
            this.ruleCheckers = [];
            return;
        }
        if (policy.version !== SUPPORTED_POLICY_VERSION) {
            // throw a standard Error for this developer failure, so this code is reusable in the browser
            throw new Error(`Expected supported policy version ${SUPPORTED_POLICY_VERSION} but got ${policy.version}.`);
        }
        this.ruleCheckers = this.buildRuleCheckers(policy);
    }
    buildRuleCheckers(policy) {
        return policy.rules.map((rule) => new PolicyAuthorizerRuleChecker(rule));
    }
    isAuthorizedTo({ action, resource, params }) {
        this.guardAgainstInvalidResource(resource);
        for (const checker of this.ruleCheckers) {
            if (checker.isAuthorized({ action, resource, params })) {
                return true;
            }
        }
        return false;
    }
    guardAgainstInvalidResource(resource) {
        if (!resource) {
            return;
        }
        if (resource.indexOf('*') !== -1) {
            throw new Exception(`Resource cannot contain asterisks, but got ${resource}. This is likely a developer error.`, 500, undefined, `Resource cannot contain asterisks, but got ${resource}.`);
        }
    }
}
//# sourceMappingURL=SecurityPolicyAuthorizer.js.map